import { useContext, useEffect, useState } from 'react';
import { ArrowUpRight } from 'lucide-react';
import { AppThemeContext } from '../contexts/AppThemeContext';
import { useTranslation } from '../contexts/TranslationContext';
import { demoSchedulingService } from '../services/demoScheduling';
import { DEFAULT_APP_THEME } from '../utils/appTheme';
import { FadeSwitch } from './FadeSwitch';
import { Loader2 } from './Icons';
import { LANDING_SECTION_TITLE_CLASS_NAME, landingSectionClassName } from './landingSectionClassName';

type CalendarStatus = 'loading' | 'ready' | 'error';

export function LandingDemoSection() {
  const { t } = useTranslation();
  const theme = useContext(AppThemeContext)?.theme ?? DEFAULT_APP_THEME;
  const [pageHostname, setPageHostname] = useState<string | null>(null);
  useEffect(() => {
    let calendarFrame: number | null = null;
    const paintFrame = window.requestAnimationFrame(() => {
      calendarFrame = window.requestAnimationFrame(() => {
        setPageHostname(window.location.hostname);
      });
    });
    return () => {
      window.cancelAnimationFrame(paintFrame);
      if (calendarFrame !== null) window.cancelAnimationFrame(calendarFrame);
    };
  }, []);
  const embedUrl = pageHostname === null ? null : demoSchedulingService.getInlineUrl(theme, pageHostname);
  const [calendar, setCalendar] = useState<{ url: string | null; status: CalendarStatus }>({ url: null, status: 'loading' });
  const status = calendar.url === embedUrl ? calendar.status : 'loading';

  return (
    <section
      id="demo"
      aria-labelledby="landing-demo-title"
      className={landingSectionClassName({ className: 'overflow-hidden py-14 sm:px-6 sm:py-24', fillAvailable: false })}
    >
      <div className="mx-auto max-w-3xl px-4 text-center">
        <h2 id="landing-demo-title" className={LANDING_SECTION_TITLE_CLASS_NAME}>
          {t('demoSection.title')}
        </h2>
        <p className="mx-auto max-w-2xl text-base leading-7 text-[rgb(var(--theme-muted))] sm:text-lg">
          {t('demoSection.description')}
        </p>
        <a
          href={demoSchedulingService.getExternalUrl()}
          target="_blank"
          rel="noopener noreferrer"
          className="brand-link brand-focus-ring mt-5 inline-flex max-w-full items-center justify-center gap-2 rounded-full px-3 py-2 text-sm font-semibold underline-offset-4 transition-colors hover:underline motion-reduce:transition-none"
        >
          <span>{t('demoSection.openCalendar')}</span>
          <ArrowUpRight className="h-4 w-4 shrink-0" aria-hidden="true" />
        </a>
      </div>

      <div id="landing-demo-calendar" className="relative mx-auto mt-8 h-[780px] w-full min-w-0 max-w-6xl sm:mt-10 sm:h-[720px]" aria-busy={status === 'loading'}>
        {embedUrl && <iframe
          key={embedUrl}
          src={embedUrl}
          title={t('demoSection.frameTitle')}
          loading="eager"
          referrerPolicy="no-referrer"
          tabIndex={status === 'ready' ? 0 : -1}
          aria-hidden={status !== 'ready'}
          className={`h-full w-full min-w-0 border-0 bg-transparent [color-scheme:light] ${theme === 'dark' ? 'mix-blend-screen' : 'mix-blend-multiply'} transition-opacity duration-500 motion-reduce:transition-none ${status === 'ready' ? 'opacity-100' : 'pointer-events-none opacity-0'}`}
          onLoad={() => setCalendar({ url: embedUrl, status: 'ready' })}
          onErrorCapture={() => setCalendar({ url: embedUrl, status: 'error' })}
        />}
        <FadeSwitch
          viewKey={status}
          className={`absolute inset-0 ${status === 'ready' ? 'pointer-events-none' : ''}`}
          childClassName="h-full"
        >
          {status !== 'ready' && (
            <div className="flex h-full flex-col items-center justify-center gap-4 px-6 text-center text-[rgb(var(--theme-muted))]" role="status" aria-live="polite">
              {status === 'loading' && <Loader2 className="h-7 w-7 animate-spin text-[rgb(var(--theme-primary-strong))] motion-reduce:animate-none" aria-hidden="true" />}
              <p className="max-w-sm text-sm leading-6">{t(status === 'error' ? 'demoSection.error' : 'demoSection.loading')}</p>
            </div>
          )}
        </FadeSwitch>
      </div>
    </section>
  );
}
